import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import API from "UserProfile/Family/API";

import FnrCheckbox from "UI/FnrCheckbox";
import { FnrOverlayTitle } from "UI/FnrOverlay";
import { FnrToggle, FnrToggleChild } from "UI/FnrToggle2";
import { SharedResizingChild } from "UI/SharedResizingArea";

import { useWindowSize } from "Utilities/Hooks/useWindowSize";

import GenderToggle from "./GenderToggle";
import MoreButton from "./MoreButton";

FamilyRelationshipPickerSection.propTypes = {
  profileUser: PropTypes.object,
  relationshipUser: PropTypes.object,
  availableRelationships: PropTypes.object,
  shouldSave: PropTypes.bool,
  setLoading: PropTypes.func,
  setSaveDisabled: PropTypes.func,
  loading: PropTypes.bool,
  handleRelationshipResponse: PropTypes.func,
  setShouldSave: PropTypes.func,
  setErrorMsg: PropTypes.func,
  fromConfirmationStep: PropTypes.bool,
};

export default function FamilyRelationshipPickerSection({
  profileUser,
  relationshipUser,
  availableRelationships,
  initialRelationshipTypeID,
  shouldSave,
  setLoading,
  setSaveDisabled,
  loading,
  handleRelationshipResponse,
  setShouldSave,
  setErrorMsg,
  fromConfirmationStep,
  saveIndirectRelationships,
}) {
  const windowSize = useWindowSize();
  const [relationshipTypeID, setRelationshipTypeID] = useState(
    initialRelationshipTypeID
  );
  const [relationshipUserGender, setRelationshipUserGender] = useState(
    relationshipUser.gender
  );

  const [triggerHoverRemeasure, setTriggerHoverRemeasure] = useState(0);

  const [isProxyContactsCheckboxChecked, setIsProxyContactsCheckboxChecked] =
    useState(false);

  useEffect(() => {
    setSaveDisabled(!relationshipTypeID);

    // Nov 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
    // eslint-disable-next-line
    if (relationshipTypeID == 2 && relationshipUser.hasEmailOrPhone)
      setIsProxyContactsCheckboxChecked(true);

    // Nov 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
    // eslint-disable-next-line
    if (relationshipTypeID == 3 && profileUser.hasEmailOrPhone)
      setIsProxyContactsCheckboxChecked(true);
    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [relationshipTypeID]);

  useEffect(() => {
    //handle the popover animation measuring
    setTimeout(() => {
      setTriggerHoverRemeasure((currentVal) => currentVal + 1);
    }, 150);
  }, []);

  const changeRelationship = (newValue) => {
    setRelationshipTypeID((currentValue) =>
      currentValue === newValue ? null : newValue
    );
  };

  useEffect(() => {
    if (!shouldSave || loading) return;

    setLoading(true);

    const saveData = {
      profileUserID: profileUser.ID,
      relationshipUserID: relationshipUser.ID,
      relationshipTypeID,
      saveIndirectRelationships: fromConfirmationStep
        ? false
        : saveIndirectRelationships,
      addProxyRelationship: isProxyContactsCheckboxChecked,
    };

    if (!relationshipUser.gender && relationshipUserGender)
      saveData.relationshipUserGender = relationshipUserGender;

    const saveCall = API.saveRelationship(saveData);

    saveCall
      .post()
      .then((resp) => {
        handleRelationshipResponse(resp, saveData);
      })
      .catch((err) => {
        if (err?.error === "pledge_conflict") {
          const campaign = err?.campaign || false;
          setErrorMsg(
            `It looks like both of these members have pledged to the ${
              campaign || "same"
            } Campaign.  You'll need to remove one of these pledges before they can be added to the same household.`
          );
        }
      });

    return () => saveCall.cancel();

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [shouldSave]);

  const moreButtonRef = useRef();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const moreMenuSelected =
    relationshipTypeID &&
    (availableRelationships?.more || []).find(
      (r) => r.ID === relationshipTypeID
    );

  const FnrToggleChildStyles = {
    style: {
      width: windowSize.width > 400 ? "50%" : "100%",
    },
  };

  if (!availableRelationships?.suggested) return null;

  return (
    <SharedResizingChild>
      <FamilyRelationshipPickerSectionWrapper>
        <FnrOverlayTitle style={{ border: "none" }}>
          {relationshipUser.fname.length > 0
            ? relationshipUser.fname
            : "This person"}{" "}
          is{" "}
          {profileUser.fname.length > 0
            ? profileUser.fname
            : "the other person"}
          {"'s..."}
        </FnrOverlayTitle>

        <FnrToggle disabled={loading} triggerResize={triggerHoverRemeasure}>
          <FnrToggleItemsCustom>
            {(availableRelationships?.suggested || []).map((r) =>
              r.ID === 0 ? (
                <FnrToggleChild
                  key={r.ID}
                  ref={moreButtonRef}
                  selected={moreMenuSelected}
                  forceHover={moreMenuOpen && !moreMenuSelected}
                  {...FnrToggleChildStyles}
                >
                  <MoreButton
                    key="more"
                    moreRelationships={availableRelationships.more}
                    changeRelationship={changeRelationship}
                    relationshipTypeID={relationshipTypeID}
                    toggleButtonRef={moreButtonRef}
                    setMoreMenuOpen={setMoreMenuOpen}
                    gender={relationshipUserGender}
                    disabled={loading}
                  />
                </FnrToggleChild>
              ) : (
                <FnrToggleChild
                  key={r.ID}
                  selected={relationshipTypeID === r.ID}
                  {...FnrToggleChildStyles}
                >
                  <RelationshipButton
                    onClick={() => (loading ? null : changeRelationship(r.ID))}
                  >
                    <RelationshipText>
                      {relationshipUserGender
                        ? r[relationshipUserGender]
                        : r.name}
                    </RelationshipText>
                  </RelationshipButton>
                </FnrToggleChild>
              )
            )}
          </FnrToggleItemsCustom>
        </FnrToggle>
        {!relationshipUser.gender && (
          <GenderHolder>
            <GenderToggle
              disabled={loading}
              title={
                "What is " +
                (relationshipUser.fname.length > 0
                  ? relationshipUser.fname
                  : "This person") +
                "'s gender?"
              }
              gender={relationshipUserGender}
              setGender={setRelationshipUserGender}
            />
          </GenderHolder>
        )}

        {
          // Nov 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
          // eslint-disable-next-line
          relationshipTypeID == 2 && relationshipUser.hasEmailOrPhone && (
            <ProxyContactsCheckboxHolder>
              <FnrCheckbox
                on={isProxyContactsCheckboxChecked}
                disabled={!profileUser.hasEmailOrPhone}
                onClick={() =>
                  setIsProxyContactsCheckboxChecked(
                    !isProxyContactsCheckboxChecked
                  )
                }
              >
                Make {relationshipUser.fname} a proxy contact for{" "}
                {profileUser.fname}
              </FnrCheckbox>
            </ProxyContactsCheckboxHolder>
          )
        }

        {
          // Nov 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
          // eslint-disable-next-line
          relationshipTypeID == 3 && profileUser.hasEmailOrPhone && (
            <ProxyContactsCheckboxHolder>
              <FnrCheckbox
                on={isProxyContactsCheckboxChecked}
                disabled={!relationshipUser.hasEmailOrPhone}
                onClick={() =>
                  setIsProxyContactsCheckboxChecked(
                    !isProxyContactsCheckboxChecked
                  )
                }
              >
                Make {profileUser.fname} a proxy contact for{" "}
                {relationshipUser.fname}
              </FnrCheckbox>
            </ProxyContactsCheckboxHolder>
          )
        }
      </FamilyRelationshipPickerSectionWrapper>
    </SharedResizingChild>
  );
}

const FamilyRelationshipPickerSectionWrapper = styled.div`
  height: auto;
  width: auto;
`;

const GenderHolder = styled.div`
  display: flex;
  margin-top: 12px;
  justify-content: center;
`;

export const RelationshipButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px;
  padding-top: 10px;
`;
export const RelationshipText = styled.div`
  font-weight: 400;
`;

const ProxyContactsCheckboxHolder = styled.div`
  display: flex;
  margin-top: 8px;
  justify-content: center;
`;

const FnrToggleItemsCustom = styled.div`
  display: flex;
  flex-wrap: wrap;
  color: #475b68;
  z-index: 100;
  position: relative;
  width: 300px;

  @media (max-width: 400px) {
    width: 200px;
  }
`;
